import React, { useEffect, useState } from 'react';
import reviewService from '../services/reviewService';
import './Reviews.css';

const Reviews = () => {
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [ratingFilter, setRatingFilter] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await reviewService.getReviews(
        ratingFilter ? { rating: ratingFilter } : {}
      );
      setReviews(data?.reviews || []);
    } catch (err) {
      console.error('Failed to load reviews', err);
      setError(
        err?.response?.data?.message ||
          'Failed to load reviews. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ratingFilter]);

  const handleDelete = async (review) => {
    if (
      !window.confirm(
        `Delete this ${review.rating}-star review by ${review.user?.full_name || 'this customer'}? This cannot be undone.`
      )
    ) {
      return;
    }

    setError('');
    setSuccess('');
    setDeletingId(review.id);

    try {
      await reviewService.deleteReview(review.id);
      setReviews((prev) => prev.filter((r) => r.id !== review.id));
      setSuccess('Review deleted.');
    } catch (err) {
      console.error('Failed to delete review', err);
      setError(
        err?.response?.data?.message ||
          'Failed to delete review. Please try again.'
      );
    } finally {
      setDeletingId(null);
    }
  };

  const formatDateTime = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  };

  return (
    <div className="reviews-page">
      <h1 className="page-title">Product Reviews</h1>
      <p className="page-subtitle">
        Ratings left by customers whose orders were delivered. Delete anything
        abusive or off-topic.
      </p>

      <div className="reviews-toolbar">
        <label>
          Rating filter
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value)}
          >
            <option value="">All ratings</option>
            {[5, 4, 3, 2, 1].map((r) => (
              <option key={r} value={r}>
                {r} star{r > 1 ? 's' : ''}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {success && (
        <div className="admin-alert admin-alert-success">{success}</div>
      )}

      {loading ? (
        <p>Loading reviews...</p>
      ) : reviews.length === 0 ? (
        <p>No reviews found.</p>
      ) : (
        <div className="reviews-table-wrapper">
          <table className="reviews-table">
            <thead>
              <tr>
                <th>Created</th>
                <th>Product</th>
                <th>Customer</th>
                <th>Rating</th>
                <th>Title</th>
                <th>Comment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reviews.map((review) => (
                <tr key={review.id}>
                  <td>{formatDateTime(review.created_at)}</td>
                  <td>{review.product?.name || '-'}</td>
                  <td>
                    <div>{review.user?.full_name || '-'}</div>
                    <div className="reviews-muted">{review.user?.email}</div>
                  </td>
                  <td>
                    <span className="reviews-stars">
                      {'★'.repeat(review.rating)}
                      <span className="reviews-stars-empty">
                        {'★'.repeat(5 - review.rating)}
                      </span>
                    </span>
                  </td>
                  <td>{review.title || '-'}</td>
                  <td className="reviews-comment-cell">
                    {review.comment || '-'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="reviews-delete-button"
                      onClick={() => handleDelete(review)}
                      disabled={deletingId === review.id}
                    >
                      {deletingId === review.id ? 'Deleting...' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default Reviews;
